import React, { useLayoutEffect, useRef, useState } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const pad = (n) => String(n).padStart(2, '0');
const GROW = 5;

export default function ExpandingCards({ items, renderCard, getKey = (item, i) => i, getLabel = () => '', scrollTo, className = '' }) {
  const root = useRef(null);
  const track = useRef(null);
  const bar = useRef(null);
  const pin = useRef(null);
  const touch = useRef(null);
  const [active, setActive] = useState(0);
  const n = items.length;

  useLayoutEffect(() => {
    const cards = gsap.utils.toArray('.ob-xc-card', track.current);
    const mm = gsap.matchMedia();

    mm.add('(prefers-reduced-motion: no-preference)', () => {
      gsap.fromTo(cards, { y: 90, rotate: (i) => (i % 2 ? 3 : -3), autoAlpha: 0 }, {
        y: 0,
        rotate: 0,
        autoAlpha: 1,
        duration: 1.3,
        ease: 'expo.out',
        stagger: 0.1,
        clearProps: 'transform,opacity,visibility',
        scrollTrigger: { trigger: track.current, start: 'top 88%', once: true },
      });
    });

    mm.add('(min-width: 900px) and (prefers-reduced-motion: no-preference)', () => {
      const st = ScrollTrigger.create({
        trigger: root.current,
        start: 'center center',
        end: () => `+=${window.innerHeight * 0.55 * (n - 1)}`,
        pin: true,
        anticipatePin: 1,
        onUpdate: (self) => {
          setActive(Math.round(self.progress * (n - 1)));
          if (bar.current) bar.current.style.transform = `scaleX(${self.progress})`;
        },
      });
      pin.current = st;
      return () => { pin.current = null; };
    });

    mm.add('(min-width: 900px)', () => {
      const size = () => {
        const el = track.current;
        const gap = parseFloat(getComputedStyle(el).columnGap) || 0;
        root.current.style.setProperty('--xc-open', `${((el.clientWidth - gap * (n - 1)) * GROW) / (GROW + n - 1)}px`);
      };
      size();
      const ro = new ResizeObserver(size);
      ro.observe(track.current);
      return () => ro.disconnect();
    });

    mm.add('(max-width: 899px) and (prefers-reduced-motion: no-preference)', () => {
      const el = track.current;
      const box = root.current;
      box.classList.add('is-driven');
      let stops = [];
      const measure = () => {
        stops = cards.map((card) => card.offsetLeft - (el.clientWidth - card.offsetWidth) / 2);
      };
      const place = (p) => {
        const f = p * (n - 1);
        const a = Math.floor(f);
        const b = Math.min(n - 1, a + 1);
        gsap.set(el, { x: -(stops[a] + (stops[b] - stops[a]) * (f - a)) });
      };
      measure();
      const st = ScrollTrigger.create({
        trigger: box,
        start: () => {
          const half = box.offsetHeight / 2;
          const y = Math.min(window.innerHeight - half - 4, Math.max(window.innerHeight / 2, 88 + half));
          return `center ${Math.round(y)}px`;
        },
        end: () => `+=${window.innerHeight * 0.6 * (n - 1)}`,
        pin: true,
        anticipatePin: 1,
        snap: { snapTo: (v) => Math.round(v * (n - 1)) / (n - 1), inertia: false, duration: { min: 0.2, max: 0.6 }, delay: 0.08, ease: 'power2.out' },
        onRefresh: (self) => { measure(); place(self.progress); },
        onUpdate: (self) => {
          place(self.progress);
          setActive(Math.round(self.progress * (n - 1)));
        },
      });
      pin.current = st;
      return () => {
        pin.current = null;
        box.classList.remove('is-driven');
        gsap.set(el, { clearProps: 'transform' });
      };
    });

    mm.add('(max-width: 899px) and (prefers-reduced-motion: reduce)', () => {
      const el = track.current;
      const onScroll = () => {
        const mid = el.scrollLeft + el.clientWidth / 2;
        let best = 0;
        let dist = Infinity;
        cards.forEach((card, i) => {
          const d = Math.abs(card.offsetLeft + card.offsetWidth / 2 - mid);
          if (d < dist) { dist = d; best = i; }
        });
        setActive(best);
        if (bar.current) bar.current.style.transform = `scaleX(${n > 1 ? best / (n - 1) : 1})`;
      };
      el.addEventListener('scroll', onScroll, { passive: true });
      onScroll();
      return () => el.removeEventListener('scroll', onScroll);
    });

    return () => mm.revert();
  }, [n]);

  const go = (i) => {
    const st = pin.current;
    if (st) {
      const y = st.start + (st.end - st.start) * (n > 1 ? i / (n - 1) : 0) + 1;
      if (scrollTo) scrollTo(y);
      else window.scrollTo({ top: y, behavior: 'smooth' });
      return;
    }
    const el = track.current;
    if (window.matchMedia('(max-width: 899px)').matches) {
      const card = el.children[i];
      el.scrollTo({ left: card.offsetLeft - (el.clientWidth - card.offsetWidth) / 2, behavior: 'smooth' });
      return;
    }
    setActive(i);
    if (bar.current) bar.current.style.transform = `scaleX(${n > 1 ? i / (n - 1) : 1})`;
  };

  const onTouchStart = (e) => {
    const t = e.touches[0];
    touch.current = { x: t.clientX, y: t.clientY };
  };

  const onTouchEnd = (e) => {
    const start = touch.current;
    touch.current = null;
    if (!start || !pin.current || !root.current.classList.contains('is-driven')) return;
    const t = e.changedTouches[0];
    const dx = t.clientX - start.x;
    const dy = t.clientY - start.y;
    if (Math.abs(dx) > 45 && Math.abs(dx) > Math.abs(dy) * 1.3) go(Math.max(0, Math.min(n - 1, active + (dx < 0 ? 1 : -1))));
  };

  return (
    <div className={`ob-xc ${className}`} ref={root}>
      <div className="ob-xc-track" ref={track} onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
        {items.map((item, i) => (
          <div
            key={getKey(item, i)}
            className={`ob-xc-card ${i === active ? 'is-active' : ''}`}
            onClick={() => { if (i !== active) go(i); }}
          >
            {renderCard(item, i, i === active)}
          </div>
        ))}
      </div>
      <div className="ob-xc-foot">
        <span className="ob-xc-count"><b>{pad(active + 1)}</b> / {pad(n)}</span>
        <span className="ob-xc-label" key={active}>{getLabel(items[active], active)}</span>
        <span className="ob-xc-bar"><i ref={bar} /></span>
        <span className="ob-xc-dots">
          {items.map((item, i) => (
            <button key={getKey(item, i)} type="button" className={i === active ? 'is-active' : ''} onClick={() => go(i)} aria-label={`Show ${getLabel(item, i)}`} />
          ))}
        </span>
      </div>
    </div>
  );
}
